import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { friendliesService } from "../../services/friendliesApi";
import CustomDateTimePicker from "../common/CustomDateTimePicker";
import TierSelector from "./TierSelector";
import { LocationModePill, OpponentTierLine, TeamCategoryPill } from "./FriendlyPills";
import {
  TEAM_CATEGORIES,
  LOCATION_MODES,
  emptyComposer,
  validateComposer,
  tierPreviewDisplay,
} from "./friendlyComposerLogic";
import { isoToDate, dateToISO, timeToDate, dateToTime } from "../../utils/friendlyDates";

// TP-Friendlies composer (design Frame 2 + Frame 8 camp). One modal, four location
// modes; choosing "Training camp" swaps the date question for a destination +
// window + multiple-matches toggle. Team category = the three brief options plus a
// free-text label ("no U21 at your club?…") in BOTH modes (D-006). Tier pills use
// the full multi-select range mode. Validation mirrors the M1 composer; on publish
// it POSTs (or PATCHes when editing) and shows a confirmation.
const DIST_PRESETS = [50, 80, 100, 150, 200, 300];

const Field = ({ label, hint, error, children }) => (
  <div className="mb-4">
    <div className="flex items-baseline justify-between">
      <label className="block text-[12.5px] font-semibold text-[#33475E] mb-1.5">{label}</label>
      {hint && <span className="text-[11px] text-[#98A2B3]">{hint}</span>}
    </div>
    {children}
    {error && <p className="mt-1 text-[11.5px] text-[#C4402F]">{error}</p>}
  </div>
);

const FriendlyComposer = ({ open, onClose, onPublished, countries = [], initialState = null, viewerClubName = "" }) => {
  const [s, setS] = useState(emptyComposer());
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [published, setPublished] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [countryQuery, setCountryQuery] = useState("");

  useEffect(() => {
    if (open) {
      setS(initialState || emptyComposer());
      setPublished(null);
      setServerError(null);
      setShowErrors(false);
      setCountryQuery("");
    }
  }, [open, initialState]);

  const set = (patch) => setS((prev) => ({ ...prev, ...patch }));
  const { valid, errors, payload } = useMemo(() => validateComposer(s), [s]);
  const isCamp = s.location_mode === "camp";

  if (!open) return null;

  const submit = async () => {
    if (!valid) {
      setShowErrors(true);
      return;
    }
    setSubmitting(true);
    setServerError(null);
    const res = s.editId
      ? await friendliesService.updateRequest(s.editId, payload)
      : await friendliesService.createRequest(payload);
    setSubmitting(false);
    if (res.success) {
      setPublished(res.data);
      if (onPublished) onPublished(res.data);
    } else {
      setServerError(res.error || "Could not publish your request.");
    }
  };

  const err = (k) => (showErrors ? errors[k] : null);
  const filteredCountries = countries
    .filter((c) => (countryQuery ? (c.name || "").toLowerCase().includes(countryQuery.toLowerCase()) : true))
    .slice(0, 40);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-start justify-center bg-black/45 p-0 sm:p-4 sm:overflow-y-auto" role="dialog" aria-modal="true">
      <div className="w-full max-w-[640px] bg-white rounded-t-2xl sm:rounded-2xl shadow-xl my-0 sm:my-6 max-h-[94vh] overflow-y-auto">
        {published ? (
          <div className="p-8 text-center">
            <span className="w-11 h-11 rounded-full bg-[#E7F6EF] flex items-center justify-center mx-auto mb-2">
              <span className="text-[#0E7B52] text-xl">✓</span>
            </span>
            <h3 className="text-[16px] font-bold text-[#0F1728]">Your friendly request is live</h3>
            <p className="text-[13px] text-[#66707F] mt-1 max-w-sm mx-auto">
              Every club can now see it and apply. It closes automatically after the match date, or the moment you mark it matched.
            </p>
            <div className="flex justify-center gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#186FE0]">Done</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF1F5]">
              <h3 className="text-[16px] font-bold text-[#0F1728]">{s.editId ? "Repost a friendly" : "Post a friendly"}</h3>
              <div className="flex items-center gap-2">
                <span className="text-[11.5px] text-[#66707F]">Published publicly as {viewerClubName || "your club"}</span>
                <button onClick={onClose} className="p-1 text-[#98A2B3] hover:text-[#475467]" aria-label="Close"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="px-6 py-5">
              {/* Which team */}
              <Field label="Which team">
                <div className="flex flex-wrap gap-1.5">
                  {TEAM_CATEGORIES.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => set({ team_category: c.value })}
                      className={`rounded-full px-3 py-1.5 text-[12.5px] font-semibold border ${s.team_category === c.value ? "border-[#2283FF] bg-[#EAF3FF] text-[#1866CC]" : "border-[#E0E6EE] text-[#475467]"}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <input
                  value={s.team_label}
                  onChange={(e) => set({ team_label: e.target.value.slice(0, 40) })}
                  placeholder="Name your team, e.g. U19, 2nd team"
                  className="mt-2 w-full bg-gray-50 border border-[#E0E6EE] rounded-lg px-3 py-2 text-[13px]"
                />
                <p className="mt-1 text-[11px] text-[#98A2B3]">Free text. No U21 at your club? Write what you call it: "U19", "2nd team"…</p>
              </Field>

              {/* Where (4 modes) */}
              <Field label="Where">
                <div className="grid grid-cols-2 gap-2">
                  {LOCATION_MODES.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => set({ location_mode: m.value })}
                      className={`text-left rounded-lg border px-3 py-2 ${s.location_mode === m.value ? "border-[#2283FF] bg-[#F0F7FF]" : "border-[#E0E6EE] hover:border-[#B5CCF0]"}`}
                    >
                      <div className="text-[12.5px] font-semibold text-[#33475E]">{m.label}</div>
                      <div className="text-[11px] text-[#98A2B3]">{m.hint}</div>
                    </button>
                  ))}
                </div>
                {s.location_mode === "travel" && (
                  <div className="mt-2">
                    <select
                      value={s.max_distance_km}
                      onChange={(e) => set({ max_distance_km: e.target.value })}
                      className="bg-gray-50 border border-[#E0E6EE] rounded-lg px-3 py-2 text-[13px]"
                    >
                      <option value="">Max distance…</option>
                      {DIST_PRESETS.map((d) => <option key={d} value={d}>up to {d} km</option>)}
                    </select>
                  </div>
                )}
              </Field>

              {/* When (non-camp) OR camp window */}
              {isCamp ? (
                <>
                  <Field label="Where is the camp?" error={err("camp_country")}>
                    <input
                      value={s.camp_country_id ? s.camp_country_name : countryQuery}
                      onChange={(e) => { setCountryQuery(e.target.value); if (s.camp_country_id) set({ camp_country_id: "", camp_country_name: "" }); }}
                      placeholder="Destination country…"
                      className="w-full bg-gray-50 border border-[#E0E6EE] rounded-lg px-3 py-2 text-[13px]"
                    />
                    {!s.camp_country_id && countryQuery && (
                      <div className="mt-1 max-h-[160px] overflow-y-auto border border-[#E0E6EE] rounded-lg">
                        {filteredCountries.map((c) => (
                          <button key={c.id} type="button"
                            onClick={() => { set({ camp_country_id: c.id, camp_country_name: c.name }); setCountryQuery(""); }}
                            className="w-full flex items-center gap-2 text-left px-3 py-1.5 text-[13px] hover:bg-gray-50">
                            {c.image_url && <img src={c.image_url} alt="" className="w-4 h-3 object-contain" referrerPolicy="no-referrer" />}
                            {c.name}
                          </button>
                        ))}
                      </div>
                    )}
                    <input
                      value={s.camp_region}
                      onChange={(e) => set({ camp_region: e.target.value.slice(0, 80) })}
                      placeholder="City / region, e.g. Belek, Antalya"
                      className="mt-2 w-full bg-gray-50 border border-[#E0E6EE] rounded-lg px-3 py-2 text-[13px]"
                    />
                    <p className="mt-1 text-[11px] text-[#98A2B3]">Your request appears under this destination. Clubs in or near it find you.</p>
                  </Field>
                  <Field label="How long are you there?" error={err("camp_window_start") || err("camp_window_end")}>
                    <div className="flex items-end gap-2">
                      <div className="flex-1"><CustomDateTimePicker value={isoToDate(s.camp_window_start)} onChange={(d) => set({ camp_window_start: dateToISO(d) })} placeholder="Camp start" dateFormat="EEE, dd MMM yyyy" /></div>
                      <span className="text-[#9AA8BC] pb-3">→</span>
                      <div className="flex-1"><CustomDateTimePicker value={isoToDate(s.camp_window_end)} onChange={(d) => set({ camp_window_end: dateToISO(d) })} placeholder="Camp end" dateFormat="EEE, dd MMM yyyy" minDate={isoToDate(s.camp_window_start)} /></div>
                    </div>
                    <label className="mt-2 flex items-center gap-2 text-[12.5px] text-[#33475E]">
                      <input type="checkbox" checked={s.camp_multiple_matches} onChange={(e) => set({ camp_multiple_matches: e.target.checked })} />
                      We're looking for more than one opponent across the window
                    </label>
                  </Field>
                </>
              ) : (
                <Field label="When">
                  <div className="inline-flex rounded-lg bg-[#EAEFF5] p-0.5 mb-2">
                    {[["fixed", "Fixed date"], ["range", "Flexible range"]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => set({ when_mode: v })}
                        className={`px-3.5 py-1.5 rounded-md text-[12.5px] font-semibold ${s.when_mode === v ? "bg-white text-[#14263D] shadow-sm" : "text-[#8A98AC]"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-end gap-2 flex-wrap">
                    <div className="flex-1 min-w-[180px]">
                      <CustomDateTimePicker value={isoToDate(s.date_start)} onChange={(d) => set({ date_start: dateToISO(d) })} placeholder={s.when_mode === "range" ? "From" : "Match date"} dateFormat="EEE, dd MMM yyyy" />
                    </div>
                    {s.when_mode === "range" && (
                      <>
                        <span className="text-[#9AA8BC] pb-3">→</span>
                        <div className="flex-1 min-w-[180px]">
                          <CustomDateTimePicker value={isoToDate(s.date_end)} onChange={(d) => set({ date_end: dateToISO(d) })} placeholder="To" dateFormat="EEE, dd MMM yyyy" minDate={isoToDate(s.date_start)} />
                        </div>
                      </>
                    )}
                    {s.when_mode === "fixed" && (
                      <div className="w-[140px]">
                        <CustomDateTimePicker value={timeToDate(s.kickoff_time)} onChange={(d) => set({ kickoff_time: dateToTime(d) })} placeholder="Kickoff" showTimeSelectOnly timeIntervals={15} />
                      </div>
                    )}
                  </div>
                  {(err("date_start") || err("date_end")) && <p className="mt-1 text-[11.5px] text-[#C4402F]">{err("date_start") || err("date_end")}</p>}
                  {s.when_mode === "fixed" && <p className="mt-1 text-[11px] text-[#98A2B3]">Kickoff time is optional.</p>}
                </Field>
              )}

              {/* Opponent level */}
              <Field label="Opponent level · league tiers" hint='Multi-select for a range → "Opponent: Tier 1-2"'>
                <TierSelector mode="multi" value={s.tier} onChange={(v) => set({ tier: v })} showThreeOrLower showAny />
              </Field>

              {/* Context */}
              <Field label="Anything opponents should know · optional" hint={`${s.context_line.length} / 200`}>
                <input
                  value={s.context_line}
                  onChange={(e) => set({ context_line: e.target.value.slice(0, 200) })}
                  placeholder="Full 90 minutes · natural grass · cost sharing possible"
                  className="w-full bg-gray-50 border border-[#E0E6EE] rounded-lg px-3 py-2 text-[13px]"
                />
              </Field>

              {/* Preview */}
              <div className="rounded-lg border border-dashed border-[#D5E6FF] bg-[#F8FBFF] p-3">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#98A2B3] mb-1.5">Preview: what other clubs see</p>
                <div className="flex items-center flex-wrap gap-2">
                  <TeamCategoryPill label={TEAM_CATEGORIES.find((c) => c.value === s.team_category)?.label} />
                  {s.team_label && <span className="text-[11.5px] text-[#66707F] font-medium">{s.team_label}</span>}
                  <LocationModePill location={{ mode: s.location_mode, region: s.camp_region, country: { name: s.camp_country_name }, is_destination: isCamp }} />
                  <OpponentTierLine display={tierPreviewDisplay(s.tier)} />
                </div>
              </div>

              {serverError && <p className="mt-3 text-[12px] text-red-500">{serverError}</p>}
            </div>

            <div className="flex justify-between items-center px-6 py-4 border-t border-[#EEF1F5]">
              <span className="text-[11px] text-[#98A2B3]">Visible to every club on TransferPitch · closes automatically after the match date.</span>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#E0E6EE] text-[13px] font-semibold text-[#33475E] hover:bg-gray-50">Cancel</button>
                <button onClick={submit} disabled={submitting} className="px-4 py-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#186FE0] disabled:opacity-50">
                  {submitting ? "Publishing…" : "Publish request"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FriendlyComposer;
